import clsx from "clsx";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { SVG_MUI } from "@ryde/assets";
import { FramerTooltip } from "@ryde/controls";
import {
  getRideMlPathIgnoredTooltipKey,
  IgnoreMLPathReasonEnum,
  NO_VALUE_DISPLAY,
  TOOLTIP_DELAY_BUTTONS,
  useHasPermissions,
} from "@ryde/shared";

import { useRideSaveRoutePath } from "../hooks";
import { SaveRouteIndicatorRight } from "./save-route-indicator.right";
import { SaveRouteIndicatorStyled } from "./save-route-indicator.styled";

interface IRideRouteButtonSave {
  stationRouteId?: number | null;
  isRouteDeleted?: boolean;
  isRoutePaused?: boolean;
  isMlPathIgnored?: boolean;
  ignoreMLPathReason?: IgnoreMLPathReasonEnum | null;
}

export const SaveRouteIndicatorFragments: FC<IRideRouteButtonSave> = ({
  stationRouteId,
  isRouteDeleted,
  isRoutePaused,
  isMlPathIgnored,
  ignoreMLPathReason,
}) => {
  const { t } = useTranslation();
  const { saveStationRouteClickHandler } = useRideSaveRoutePath();
  const { hasPermissions: hasUpdatePermission } = useHasPermissions({
    entity: "stationRoute",
    action: "update",
  });

  const tooltipContent = useMemo(() => {
    if (isMlPathIgnored) {
      return t(getRideMlPathIgnoredTooltipKey(ignoreMLPathReason));
    }

    if (!stationRouteId && hasUpdatePermission) {
      return t("ride.SAVE_ROUTE.SAVE_PATH");
    }

    if (stationRouteId && isRouteDeleted) {
      return t("ride.SAVE_ROUTE.ROUTE_IS_DELETED", {
        id: stationRouteId ?? NO_VALUE_DISPLAY,
      });
    }

    if (stationRouteId) {
      return t("ride.SAVE_ROUTE.SAVED_PATH_ID", {
        id: stationRouteId ?? NO_VALUE_DISPLAY,
      });
    }

    return t("ride.SAVE_ROUTE.SAVE_PATH");
  }, [
    hasUpdatePermission,
    ignoreMLPathReason,
    isMlPathIgnored,
    isRouteDeleted,
    stationRouteId,
    t,
  ]);

  const buttonText = useMemo(() => {
    if (isMlPathIgnored) {
      return t("ride.SAVE_ROUTE.IGNORED_PATH");
    }

    if (
      hasUpdatePermission &&
      (!stationRouteId || isRouteDeleted) &&
      !isRoutePaused
    ) {
      return t("ride.SAVE_ROUTE.SAVE_PATH");
    }

    if (hasUpdatePermission && isRoutePaused) {
      return t("stationRoute.formLabel.reactivatePath");
    }

    return t("ride.SAVE_ROUTE.SAVED_PATH");
  }, [
    hasUpdatePermission,
    isMlPathIgnored,
    isRouteDeleted,
    isRoutePaused,
    stationRouteId,
    t,
  ]);

  const shouldOpenStationRouteDrawer = Boolean(
    hasUpdatePermission ||
    (stationRouteId && !isRouteDeleted) ||
    isMlPathIgnored,
  );
  return (
    <SaveRouteIndicatorStyled
      onClick={
        shouldOpenStationRouteDrawer
          ? () => saveStationRouteClickHandler(isRouteDeleted)
          : undefined
      }
      className={clsx({
        isYellowState: Boolean(
          stationRouteId &&
          !isRoutePaused &&
          !isRouteDeleted &&
          !isMlPathIgnored,
        ),
        isIgnoredState: Boolean(isMlPathIgnored),
        isDeletedState: Boolean(stationRouteId && isRouteDeleted),
        pointerIcon: shouldOpenStationRouteDrawer,
      })}
    >
      <FramerTooltip
        content={tooltipContent}
        mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
      >
        {buttonText}
      </FramerTooltip>

      {Boolean(
        stationRouteId && !isRouteDeleted && !isRoutePaused && !isMlPathIgnored,
      ) && <SVG_MUI.StarGoldSVG className="iconYellow" />}
      {Boolean(isMlPathIgnored) && (
        <SVG_MUI.StarGreySVG className="indicatorIcon iconIgnored" />
      )}
      {Boolean(
        (!stationRouteId || isRouteDeleted) &&
        !isRoutePaused &&
        !isMlPathIgnored,
      ) && (
        <SVG_MUI.StarGreySVG
          className={clsx({
            indicatorIcon: true,
            iconDeleted: isRouteDeleted,
            iconGrey: !isRouteDeleted,
          })}
        />
      )}
      {Boolean(isRoutePaused && !isRouteDeleted && !isMlPathIgnored) && (
        <SVG_MUI.PathPausedSVG className="indicatorIcon" />
      )}
      {hasUpdatePermission && (
        <SaveRouteIndicatorRight
          isRouteDeleted={isRouteDeleted}
          isRoutePaused={isRoutePaused}
          isMlPathIgnored={isMlPathIgnored}
          stationRouteId={stationRouteId}
        />
      )}
    </SaveRouteIndicatorStyled>
  );
};
